import { createFileRoute } from '@tanstack/react-router'
import { Container, Grid } from '@mantine/core'
import { SiGrafana, SiMural } from 'react-icons/si'
import { z } from 'zod'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { notifications } from '@mantine/notifications'

import { useTranslation } from 'react-i18next'
import { requireAuth } from '@/utils/auth'
import { StatCard } from '@/components/StatCard'
import { HotlineCallsTable } from '@/components/HotlineCallsTable'
import { CurrentShiftsTable } from '@/components/CurrentShiftsTable'
import { SectionTitle } from '@/components/Common/SectionTitle'
import { PermissionGuard } from '@/utils/permissions'
import { useAuthStore } from '@/stores/auth'
import StatusDashboard from '@/components/StatusDashboard'
import { usePageTitle } from '@/hooks/usePageTitle'

const searchSchema = z.object({
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  error: z.string().optional(),
})

export const Route = createFileRoute('/')({
  beforeLoad: requireAuth,
  validateSearch: searchSchema,
  component: DashboardComponent,
})

function DashboardComponent() {
  usePageTitle()
  const { t } = useTranslation('dashboard')
  const now = dayjs()
  const endDate = now.toDate()
  const startDate = now.subtract(24, 'hour').toDate()
  const { error } = Route.useSearch()
  const { userPermissions } = useAuthStore()

  useEffect(() => {
    if (error === 'insufficient_permissions') {
      notifications.show({
        title: t('accessDenied'),
        message: t('noPermissionToAccessResource'),
        color: 'orange',
      })
    }
  }, [error, t])

  return (
    <>
      <Container size="xl" py="xl">
        <StatusDashboard />
        <PermissionGuard userPermissions={userPermissions} resource="links" requireAll>
          <SectionTitle order={1}>{t('dispatchingLinks')}</SectionTitle>
          <Grid mb="var(--spacing-xxl)">
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <StatCard
                icon={SiMural}
                iconColor="red"
                text={t('hotlineProcess')}
                link="https://app.mural.co/t/aliumworkspace6202/m/iteraidfleetmanagement2939/1686069898695/111d8484eda3bd09b539326d15f343073ffb29f5"
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <StatCard
                icon={SiGrafana}
                iconColor="#ff6b35"
                text={t('grafanaDispatching')}
                link="https://grafana.citysense.io/d/cdwo0bo0l1af4e/dispatching"
                useGradient={true}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <StatCard
                icon={SiGrafana}
                text={t('grafanaLogs')}
                iconColor="#ff6b35"
                link="https://grafana.citysense.io/d/KgqkRunVz/car-logs"
                useGradient={true}
              />
            </Grid.Col>
          </Grid>
        </PermissionGuard>

        <PermissionGuard userPermissions={userPermissions} resource="hotline">
          <SectionTitle order={2}>{t('hotlineCalls24h')}</SectionTitle>
          <HotlineCallsTable startDate={startDate} endDate={endDate} hideConfig tableKey="hotlineTableColumnsHome" />
        </PermissionGuard>

        <PermissionGuard userPermissions={userPermissions} resource="shifts">
          <SectionTitle order={2} style={{ marginTop: '4rem' }}>
            {t('currentUpcomingShifts')}
          </SectionTitle>
          <CurrentShiftsTable />
        </PermissionGuard>
      </Container>
    </>
  )
}
